/**
 * @file Display formatting helpers for Shared Calendar Leave detail.
 */

import {
    formatDate,
    formatDateTime,
} from "../../shared/utils/dateUtils.js";

const APPROVAL_DECISION_LABELS = {
    approved: "Approved",
    rejected: "Rejected",
};
const APPROVAL_ACTION_LABELS = {
    create: "create request",
    edit: "edit request",
    cancel: "cancel request",
};

/**
 * Formats a Leave date span for display.
 * @param {object} data Leave-like data.
 * @returns {string} Formatted date range.
 */
export function formatLeaveDateRange(data = {}) {
    const startDate = data.startDate || "";
    const endDate = data.endDate || startDate;
    const formattedStart = formatDate(startDate) || startDate;
    const formattedEnd = formatDate(endDate) || endDate;
    return startDate === endDate ?
        formattedStart :
        `${formattedStart} - ${formattedEnd}`;
}

/**
 * Formats an overlap span with an explicit overlap label.
 * @param {object} data Leave overlap data.
 * @returns {string} Labelled overlap date text.
 */
export function formatLeaveOverlapDateLabel(data = {}) {
    const startDate = data.startDate || "";
    const endDate = data.endDate || startDate;
    const dateRange = formatLeaveDateRange({ startDate, endDate });
    const label = endDate && endDate !== startDate ?
        "Overlapping dates" :
        "Overlapping date";
    return `${label}: ${dateRange}`;
}

/**
 * Converts a workflow status into Title Case display text.
 * @param {string} value Status value.
 * @returns {string} Display label.
 */
export function formatLeaveStatusLabel(value) {
    return String(value || "pending")
        .replace(/_/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
        .join(" ");
}

/**
 * Formats the users who approved a Leave request.
 * @param {object} data Leave-like data.
 * @param {Function} getUserName User name resolver.
 * @returns {string} Approved-by display text.
 */
export function formatLeaveApprovedBy(data = {}, getUserName = (id) => id) {
    if (data.approvedBy === "system") {return "Not applicable";}
    const history = Array.isArray(data.approvalHistory) ?
        data.approvalHistory :
        [];
    const approverIds = history.length > 0 ?
        history :
        [data.approvedBy].filter(Boolean);
    return approverIds.length > 0 ?
        approverIds.map(getUserName).join(", ") :
        "Not approved";
}

/**
 * Formats timestamped Leave approval events for display.
 * @param {object} data Leave-like data.
 * @param {Function} getUserName User name resolver.
 * @returns {Array<object>} Formatted approval events.
 */
export function formatLeaveApprovalEvents(data = {}, getUserName = (id) => id) {
    const events = Array.isArray(data.approvalEvents) ?
        data.approvalEvents :
        [];
    return events
        .map((event) => {
            const userId = String(event?.userId || "").trim();
            const decisionKey = String(event?.decision || "").trim();
            if (!userId || !decisionKey) {return null;}

            const decision = APPROVAL_DECISION_LABELS[decisionKey] ||
                formatLeaveStatusLabel(decisionKey);
            const action = APPROVAL_ACTION_LABELS[event?.requestedAction] || "";
            return {
                actorName: userId === "system" ? "System" : getUserName(userId),
                label: action ? `${decision} ${action}` : decision,
                at: formatDateTime(event?.at),
            };
        })
        .filter(Boolean);
}

/**
 * Formats the Leave submission timestamp.
 * @param {object} data Leave-like data.
 * @returns {string} Submitted display text.
 */
export function formatLeaveSubmittedAt(data = {}) {
    return formatDateTime(data.createdAt);
}

/**
 * Formats the final Leave approval timestamp.
 * @param {object} data Leave-like data.
 * @returns {string} Approved-on display text.
 */
export function formatLeaveApprovedOn(data = {}) {
    return formatDateTime(data.approvedAt) || "Not approved";
}
